import { z } from 'zod';
/**
 * QA 2026-10-01 F-01 / F-25 — the one industry list, used by New account, Edit
 * account, the Accounts list filter and import normalization.
 *
 * Values are stored as snake_case codes. The first ten existed before (kept
 * unchanged); the last eleven were added by the 2026-10-01 decision. Labels
 * are explicit, not derived, so a code can be renamed on screen without a data
 * change.
 */
export const INDUSTRY_LABELS = {
    staffing_agency: 'Staffing Agency',
    consulting_firm: 'Consulting Firm',
    recruiting_firm: 'Recruiting Firm',
    professional_services: 'Professional Services',
    tech_company: 'Tech Company',
    finance: 'Finance',
    healthcare: 'Healthcare',
    manufacturing: 'Manufacturing',
    retail: 'Retail',
    other: 'Other',
    aerospace_defense: 'Aerospace and Defense',
    government_contractor: 'Government Contractor',
    automotive: 'Automotive',
    energy_utilities: 'Energy and Utilities',
    construction_engineering: 'Construction and Engineering',
    logistics_transportation: 'Logistics and Transportation',
    education: 'Education',
    nonprofit: 'Nonprofit',
    legal: 'Legal',
    real_estate: 'Real Estate',
    hospitality: 'Hospitality',
};
export const INDUSTRY_VALUES = Object.keys(INDUSTRY_LABELS);
export const IndustrySchema = z.enum(INDUSTRY_VALUES);
// The element type is spelled out: TypeScript infers a type predicate from the
// `!== 'other'` filter, which would narrow the array and then reject the
// "Other" entry appended last.
export const INDUSTRY_OPTIONS = [
    ...INDUSTRY_VALUES.filter((v) => v !== 'other')
        .map((value) => ({ value, label: INDUSTRY_LABELS[value] }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    { value: 'other', label: INDUSTRY_LABELS.other },
];
export function isIndustry(value) {
    return typeof value === 'string' && Object.prototype.hasOwnProperty.call(INDUSTRY_LABELS, value);
}
/**
 * The label for a stored value. A value outside the list (an older import kept
 * as typed) is shown as stored, so nothing disappears from the screen.
 */
export function industryLabelOf(value) {
    if (value === null || value === undefined || value.trim() === '')
        return null;
    return isIndustry(value) ? INDUSTRY_LABELS[value] : value;
}
/** Common ways an industry is typed in a spreadsheet, mapped to a code. */
const INDUSTRY_ALIASES = {
    staffing: 'staffing_agency',
    'staffing firm': 'staffing_agency',
    consulting: 'consulting_firm',
    consultancy: 'consulting_firm',
    recruiting: 'recruiting_firm',
    recruitment: 'recruiting_firm',
    'executive search': 'recruiting_firm',
    tech: 'tech_company',
    technology: 'tech_company',
    software: 'tech_company',
    saas: 'tech_company',
    'information technology': 'tech_company',
    it: 'tech_company',
    financial: 'finance',
    'financial services': 'finance',
    banking: 'finance',
    insurance: 'finance',
    'health care': 'healthcare',
    medical: 'healthcare',
    hospital: 'healthcare',
    aerospace: 'aerospace_defense',
    defense: 'aerospace_defense',
    defence: 'aerospace_defense',
    'a d': 'aerospace_defense',
    'aerospace defense': 'aerospace_defense',
    aviation: 'aerospace_defense',
    'government contracting': 'government_contractor',
    'gov contractor': 'government_contractor',
    'federal contractor': 'government_contractor',
    'government services': 'government_contractor',
    auto: 'automotive',
    'auto parts': 'automotive',
    energy: 'energy_utilities',
    utilities: 'energy_utilities',
    utility: 'energy_utilities',
    'oil and gas': 'energy_utilities',
    'oil gas': 'energy_utilities',
    construction: 'construction_engineering',
    engineering: 'construction_engineering',
    'engineering services': 'construction_engineering',
    logistics: 'logistics_transportation',
    transportation: 'logistics_transportation',
    trucking: 'logistics_transportation',
    'supply chain': 'logistics_transportation',
    'higher education': 'education',
    'k 12': 'education',
    'non profit': 'nonprofit',
    'not for profit': 'nonprofit',
    charity: 'nonprofit',
    'law firm': 'legal',
    'legal services': 'legal',
    'real estate services': 'real_estate',
    property: 'real_estate',
    'property management': 'real_estate',
    hotel: 'hospitality',
    hotels: 'hospitality',
    restaurant: 'hospitality',
    restaurants: 'hospitality',
    'food service': 'hospitality',
};
function key(text) {
    return text
        .toLowerCase()
        .replace(/&/g, ' and ')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\band\b/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
const BY_KEY = (() => {
    const m = new Map();
    for (const v of INDUSTRY_VALUES) {
        m.set(key(v), v);
        m.set(key(INDUSTRY_LABELS[v]), v);
    }
    for (const [alias, v] of Object.entries(INDUSTRY_ALIASES))
        m.set(key(alias), v);
    return m;
})();
/**
 * Map typed text (a code, a label in any case, or a common alias such as
 * "Aerospace & Defense" or "non-profit") to an industry code. Returns
 * undefined when the text does not match, so the caller decides what to keep.
 */
export function normalizeIndustry(text) {
    if (text === null || text === undefined)
        return undefined;
    const k = key(text);
    if (!k)
        return undefined;
    return BY_KEY.get(k);
}
//# sourceMappingURL=industry.js.map